/**
 * The browser side of notifications: asking permission, subscribing to Web
 * Push through the service worker, keeping the server's copy of the
 * subscription current — and the fallback for when Web Push cannot work.
 *
 * A subscription belongs to a browser, not an account. Signing out therefore
 * only forgets it on the server; the browser keeps it, and the next sign-in
 * `sync`s it back under whoever that is — so notifications follow the
 * person using this device without asking permission twice.
 *
 * Web Push always goes through the browser maker's push service — Google's
 * FCM for Chrome and its relatives, Apple's for Safari — and a site cannot
 * pick another. On a network that cannot reach it, `subscribe()` fails or
 * hangs, or an existing subscription silently stops delivering. The socket
 * does not depend on either, and it carries every message of every room the
 * person is in, so while the app is open — even hidden in a background tab
 * or a minimised window — the app shows the notification itself
 * (`showLocalNotification`). With the app closed, only Web Push can reach
 * the device.
 */

import * as auth from "@/chat/auth"

export type PushState =
  /** No Notification API at all: Safari in an iOS tab, an old browser. */
  | "unsupported"
  /** The server has no VAPID keys. */
  | "unavailable"
  /** Permission was refused; only the browser's site settings can undo that. */
  | "denied"
  | "off"
  /** Web Push is subscribed; the app also notifies while it is open. */
  | "on"
  /**
   * Notifications are on, but Web Push could not be set up — the push
   * service is unreachable or missing — so they arrive only while the app
   * is open.
   */
  | "local"
  /** A request is in flight. */
  | "busy"

export function pushSupported(): boolean {
  return (
    notificationsSupported() &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  )
}

export function notificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window
}

/**
 * Whether the person has notifications switched on, in this browser. The
 * permission alone cannot say: a site can never revoke it, so turning the
 * switch off has to be remembered separately.
 */
const ENABLED_KEY = "chatforge:notifications"

function readEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === "on"
  } catch {
    return false
  }
}

function writeEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, enabled ? "on" : "off")
  } catch {
    // Private mode: the switch then lasts as long as the page.
  }
}

/** Fails a promise that has not settled in `ms`, for calls that can hang. */
function within<T>(promise: Promise<T>, ms: number, what: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`${what} timed out`)), ms)
    ),
  ])
}

/**
 * Waits for the active registration. `ready` never rejects, so the wait is
 * capped: with no worker (a dev server without the plugin, a blocked script)
 * the caller should learn that rather than hang.
 */
async function registration(): Promise<ServiceWorkerRegistration> {
  return within(navigator.serviceWorker.ready, 10_000, "service worker")
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null
  try {
    return await (await registration()).pushManager.getSubscription()
  } catch {
    return null
  }
}

/** What the switch should show right now, before any request is made. */
export async function readPushState(): Promise<PushState> {
  if (!notificationsSupported()) return "unsupported"
  if (Notification.permission === "denied") return "denied"
  if (await currentSubscription()) {
    // A subscription made before the switch was remembered counts as on —
    // those are exactly the devices whose push may have stopped arriving
    // and that most need the fallback.
    writeEnabled(true)
    return "on"
  }
  return Notification.permission === "granted" && readEnabled()
    ? "local"
    : "off"
}

/** `applicationServerKey` wants bytes; the server hands out base64url. */
function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url + "=".repeat((4 - (base64url.length % 4)) % 4)
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"))
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

export class PushDenied extends Error {
  constructor() {
    super("Уведомления запрещены в настройках браузера.")
    this.name = "PushDenied"
  }
}

/**
 * A browser cut off from its push service does not always fail
 * `subscribe()` — Chrome can wait on FCM for minutes. Past this, the switch
 * settles for notifications while the app is open.
 */
const SUBSCRIBE_TIMEOUT_MS = 15_000

/** Subscribes (or reuses the subscription) and registers it with the server. */
async function subscribe(token: string): Promise<void> {
  if (!pushSupported()) throw new Error("no Push API")

  const key = await auth.pushKey()
  const reg = await registration()
  const subscription =
    (await reg.pushManager.getSubscription()) ??
    (await within(
      reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(key),
      }),
      SUBSCRIBE_TIMEOUT_MS,
      "push subscription"
    ))

  await auth.subscribePush(token, subscription.toJSON())
}

/**
 * Asks, then tries Web Push. Must run from a user gesture: browsers (iOS
 * above all) ignore a permission prompt that comes from nowhere. Rejects
 * only with `PushDenied`; anything that stops Web Push — no Push API, no
 * VAPID keys on the server, an unreachable push service — leaves
 * notifications on in the `local` state instead of failing the switch.
 */
export async function enablePush(token: string): Promise<PushState> {
  const permission = await Notification.requestPermission()
  if (permission !== "granted") throw new PushDenied()

  writeEnabled(true)

  try {
    await subscribe(token)
    return "on"
  } catch {
    return "local"
  }
}

/**
 * Tries Web Push again for a browser that is in the `local` state: the
 * network may reach the push service now. Needs no gesture, since the
 * permission is already granted. Quiet on failure.
 */
export async function retryPush(token: string): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== "granted") return false
  if (!readEnabled()) return false

  try {
    await subscribe(token)
    return true
  } catch {
    return false
  }
}

/** Switches notifications off: forgets the subscription on both sides. */
export async function disablePush(token: string): Promise<void> {
  writeEnabled(false)

  const subscription = await currentSubscription()
  if (!subscription) return

  // Server first: if it fails the browser still holds the subscription and
  // the switch stays on, which is the truthful state.
  await auth.unsubscribePush(token, subscription.endpoint)
  await subscription.unsubscribe()
}

/**
 * Re-registers whatever subscription the browser holds under `token`'s
 * account. Run at sign-in and when the worker reports the push service
 * rotated the endpoint. Quiet on failure: it will run again next time.
 */
export async function syncPush(token: string): Promise<void> {
  const subscription = await currentSubscription()
  if (!subscription) return

  try {
    await auth.subscribePush(token, subscription.toJSON())
  } catch {
    // The server may have turned push off, or be unreachable; nothing to do.
  }
}

/** Server-side only; the browser's subscription outlives the account. */
export async function forgetPush(token: string): Promise<void> {
  const subscription = await currentSubscription()
  if (!subscription) return

  try {
    await auth.unsubscribePush(token, subscription.endpoint)
  } catch {
    // Best effort; the subscription is re-pointed at the next sign-in anyway.
  }
}

/** Closes the system notifications shown for `roomId`, once it is on screen. */
export async function dismissNotifications(roomId: string): Promise<void> {
  if (!pushSupported()) return
  try {
    const reg = await registration()
    for (const n of await reg.getNotifications({ tag: `room:${roomId}` })) {
      n.close()
    }
  } catch {
    // No worker: nothing was shown by one either.
  }
}

/** Whether a notification from the app itself should be shown at all. */
export function localNotificationsEnabled(): boolean {
  return (
    notificationsSupported() &&
    Notification.permission === "granted" &&
    readEnabled()
  )
}

/**
 * Shows a notification from the page, for a message that came over the
 * socket. It carries the same tag and data as one from a push, so the two
 * replace rather than stack — a device that gets both still alerts once —
 * and a tap is handled by the worker's `notificationclick` either way.
 *
 * Through the worker's registration where there is one: Chrome on Android
 * refuses `new Notification()` outright.
 */
export async function showLocalNotification(
  roomId: string,
  title: string,
  body: string
): Promise<void> {
  const options: NotificationOptions = {
    body,
    tag: `room:${roomId}`,
    icon: "/pwa-192x192.png",
    badge: "/pwa-192x192.png",
    lang: "ru",
    data: { roomId },
  }

  if ("serviceWorker" in navigator) {
    try {
      const reg = await within(
        navigator.serviceWorker.ready,
        2_000,
        "service worker"
      )
      await reg.showNotification(title, options)
      return
    } catch {
      // No worker (a dev server without one): fall through to the page.
    }
  }

  try {
    const notification = new Notification(title, options)
    notification.onclick = () => {
      window.focus()
      window.dispatchEvent(
        new CustomEvent("chatforge:openRoom", { detail: roomId })
      )
      notification.close()
    }
  } catch {
    // Not allowed from a page here; nothing else to try.
  }
}
